import {
  LINK_ARTIFACT_CONTENT_TYPE,
  LINK_ARTIFACT_MAGIC,
  LINK_IV_BYTES,
  LINK_KEY_BYTES,
} from "./constants";
import { decodeFixedBase64Url, encodeBase64Url } from "./base64url";
import { generateEncryptionIv, generateEncryptionKeyBytes } from "./capabilities";
import { copyToArrayBuffer, sha256Base64Url } from "./digest";

const textEncoder = new TextEncoder();
const artifactMagic = textEncoder.encode(LINK_ARTIFACT_MAGIC);
const additionalData = textEncoder.encode("quotation-link-artifact/v1");
const AES_GCM_TAG_BYTES = 16;

export function encryptedLinkArtifactSize(envelopeBytes: number): number {
  if (!Number.isSafeInteger(envelopeBytes) || envelopeBytes < 0) {
    throw new Error("Envelope size must be a non-negative safe integer.");
  }
  return artifactMagic.byteLength + LINK_IV_BYTES + AES_GCM_TAG_BYTES + envelopeBytes;
}

export interface EncryptedLinkArtifact {
  artifact: Blob;
  decryptionKey: string;
  ciphertextSha256: string;
  byteLength: number;
}

export class LinkIntegrityError extends Error {
  public constructor(message = "The encrypted file failed integrity checks.") {
    super(message);
    this.name = "LinkIntegrityError";
  }
}

function hasMagic(bytes: Uint8Array): boolean {
  return artifactMagic.every((byte, index) => bytes[index] === byte);
}

export async function encryptLinkEnvelope(
  envelope: Blob,
): Promise<EncryptedLinkArtifact> {
  const keyBytes = generateEncryptionKeyBytes();
  const iv = generateEncryptionIv();
  const key = await crypto.subtle.importKey(
    "raw",
    copyToArrayBuffer(keyBytes),
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  let encrypted: ArrayBuffer;
  try {
    encrypted = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: copyToArrayBuffer(iv),
        additionalData: copyToArrayBuffer(additionalData),
        tagLength: 128,
      },
      key,
      await envelope.arrayBuffer(),
    );
  } finally {
    // The exported capability remains in the returned string; discard this
    // extra mutable copy as soon as the CryptoKey has been imported.
  }

  const decryptionKey = encodeBase64Url(keyBytes);
  keyBytes.fill(0);
  const artifact = new Blob(
    [copyToArrayBuffer(artifactMagic), copyToArrayBuffer(iv), encrypted],
    {
      type: LINK_ARTIFACT_CONTENT_TYPE,
    },
  );

  return {
    artifact,
    decryptionKey,
    ciphertextSha256: await sha256Base64Url(artifact),
    byteLength: artifact.size,
  };
}

export async function decryptLinkArtifact(
  artifact: Blob,
  decryptionKey: string,
  expectedCiphertextSha256?: string,
): Promise<Blob> {
  if (
    expectedCiphertextSha256 !== undefined &&
    (await sha256Base64Url(artifact)) !== expectedCiphertextSha256
  ) {
    throw new LinkIntegrityError("The encrypted download digest did not match.");
  }

  const bytes = new Uint8Array(await artifact.arrayBuffer());
  const headerLength = artifactMagic.byteLength + LINK_IV_BYTES;
  if (bytes.byteLength <= headerLength + 16 || !hasMagic(bytes)) {
    throw new LinkIntegrityError("The encrypted download format is invalid.");
  }

  const keyBytes = decodeFixedBase64Url(
    decryptionKey,
    LINK_KEY_BYTES,
    "Decryption key",
  );
  const key = await crypto.subtle.importKey(
    "raw",
    copyToArrayBuffer(keyBytes),
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );
  keyBytes.fill(0);

  try {
    const decrypted = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: copyToArrayBuffer(bytes.slice(artifactMagic.byteLength, headerLength)),
        additionalData: copyToArrayBuffer(additionalData),
        tagLength: 128,
      },
      key,
      copyToArrayBuffer(bytes.slice(headerLength)),
    );
    return new Blob([decrypted]);
  } catch {
    throw new LinkIntegrityError("The encrypted download could not be authenticated.");
  }
}
